import React, { useState, useEffect } from 'react';
import { complianceService } from '../../services/api';

interface Property {
    id: string;
    address?: { line1?: string; postcode?: string };
    name?: string;
}

interface Group {
    id: string;
    name: string;
    parentGroupId: string | null;
    propertyIds: string[];
}

interface PropertyGroupManagerProps {
    onClose: () => void;
    onRefresh: () => void;
}

const PropertyGroupManager: React.FC<PropertyGroupManagerProps> = ({ onClose, onRefresh }) => {
    const [groups, setGroups] = useState<Group[]>([]);
    const [properties, setProperties] = useState<Property[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    
    // Create/Edit State
    const [isCreating, setIsCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [newParentId, setNewParentId] = useState<string>('');

    // Property Selection State
    const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
    const [selectedPropertyIds, setSelectedPropertyIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [groupsRes, propsRes] = await Promise.all([
                complianceService.getPropertyGroups(),
                complianceService.getProperties()
            ]);
            
            if (groupsRes.data?.success) {
                setGroups(groupsRes.data.data);
            }
            
            const propsData = Array.isArray(propsRes.data) ? propsRes.data : (propsRes.data?.data || []);
            setProperties(propsData.filter((p: any) => p.id && (p.address || p.name)));
            
        } catch (err) {
            console.error('[PropertyGroupManager] Fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateGroup = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newName.trim()) return;

        setSaving(true);
        try {
            const res = await complianceService.createPropertyGroup({
                name: newName,
                parentGroupId: newParentId || null
            });
            if (res.data?.success) {
                setNewName('');
                setNewParentId('');
                setIsCreating(false);
                fetchData();
                onRefresh();
            }
        } catch (err) {
            console.error('[PropertyGroupManager] Create error:', err);
            alert('Failed to create group');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteGroup = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this group?')) return;

        try {
            const res = await complianceService.deletePropertyGroup(id);
            if (res.data?.success) {
                fetchData();
                onRefresh();
            }
        } catch (err) {
            console.error('[PropertyGroupManager] Delete error:', err);
            alert('Failed to delete group');
        }
    };

    const startEditingProperties = (group: Group) => {
        setEditingGroupId(group.id);
        setSelectedPropertyIds(new Set(group.propertyIds || []));
    };

    const toggleProperty = (id: string) => {
        const next = new Set(selectedPropertyIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedPropertyIds(next);
    };

    const saveProperties = async () => {
        if (!editingGroupId) return;
        setSaving(true);
        try {
            await complianceService.updatePropertyGroup(editingGroupId, {
                propertyIds: Array.from(selectedPropertyIds)
            });
            setEditingGroupId(null);
            fetchData();
            onRefresh();
        } catch (err) {
            console.error('[PropertyGroupManager] Save props error:', err);
            alert('Failed to save property assignments');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[100] flex items-center justify-center">
                <div className="hp-card bg-white p-8">
                    <div className="hp-spinner mx-auto mb-4"></div>
                    <p className="text-slate-600">Loading portfolio settings...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <div>
                        <h2 className="text-xl font-bold text-slate-900">Portfolio Structure</h2>
                        <p className="text-sm text-slate-500">Configure regions, blocks, and property groups</p>
                    </div>
                    <button 
                        onClick={onClose}
                        className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-slate-200 transition-colors"
                    >
                        <i className="fas fa-times text-slate-400"></i>
                    </button>
                </div>

                <div className="flex-1 overflow-hidden flex">
                    {/* Groups List (Left Side) */}
                    <div className="w-1/2 border-r border-slate-100 p-6 overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-bold text-slate-700">Groups</h3>
                            <button 
                                onClick={() => setIsCreating(!isCreating)}
                                className="text-xs font-bold text-emerald-600 uppercase tracking-wider hover:text-emerald-700"
                            >
                                {isCreating ? 'Cancel' : '+ New Group'}
                            </button>
                        </div>

                        {isCreating && (
                            <form onSubmit={handleCreateGroup} className="mb-6 bg-slate-50 p-4 rounded-xl border border-slate-200">
                                <div className="space-y-3">
                                    <div>
                                        <label className="text-[10px] font-bold text-slate-500 uppercase">Group Name</label>
                                        <input 
                                            type="text" 
                                            value={newName}
                                            onChange={(e) => setNewName(e.target.value)}
                                            placeholder="e.g. London Region"
                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold text-slate-500 uppercase">Parent Group (Optional)</label>
                                        <select 
                                            value={newParentId}
                                            onChange={(e) => setNewParentId(e.target.value)}
                                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none"
                                        >
                                            <option value="">None (Top Level)</option>
                                            {groups.map(g => (
                                                <option key={g.id} value={g.id}>{g.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <button 
                                        type="submit"
                                        disabled={saving}
                                        className="w-full bg-emerald-600 text-white py-2 rounded-lg text-sm font-bold hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                                    >
                                        {saving ? 'Creating...' : 'Create Group'}
                                    </button>
                                </div>
                            </form>
                        )}

                        <div className="space-y-2">
                            {groups.map(group => (
                                <div 
                                    key={group.id}
                                    className={`p-3 rounded-xl border transition-all cursor-pointer ${editingGroupId === group.id ? 'border-emerald-500 bg-emerald-50 shadow-md' : 'border-slate-100 bg-white hover:border-slate-300'}`}
                                    onClick={() => startEditingProperties(group)}
                                >
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <div className="font-bold text-slate-900">{group.name}</div>
                                            <div className="text-[10px] text-slate-500 italic">
                                                {group.propertyIds?.length || 0} properties assigned
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button 
                                                onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group.id); }}
                                                className="h-8 w-8 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                                            >
                                                <i className="fas fa-trash-alt text-xs"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {groups.length === 0 && !isCreating && (
                                <div className="text-center py-12 text-slate-400 italic text-sm">
                                    No groups created yet.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Property Assignment (Right Side) */}
                    <div className="w-1/2 p-6 flex flex-col bg-slate-50">
                        {editingGroupId ? (
                            <>
                                <h3 className="font-bold text-slate-700 mb-2">
                                    Assign Properties to {groups.find(g => g.id === editingGroupId)?.name}
                                </h3>
                                <p className="text-xs text-slate-500 mb-4">Select properties to include in this group.</p>
                                
                                <div className="flex-1 overflow-y-auto space-y-1 mb-4 pr-2">
                                    {properties.map(prop => (
                                        <div 
                                            key={prop.id}
                                            className={`flex items-center gap-3 p-2 rounded-lg transition-colors cursor-pointer ${selectedPropertyIds.has(prop.id) ? 'bg-emerald-100' : 'hover:bg-slate-200'}`}
                                            onClick={() => toggleProperty(prop.id)}
                                        >
                                            <input 
                                                type="checkbox" 
                                                checked={selectedPropertyIds.has(prop.id)}
                                                onChange={() => {}} // Controlled by div click
                                                className="h-4 w-4 rounded text-emerald-600 focus:ring-emerald-500"
                                            />
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium text-slate-800 truncate">
                                                    {prop.address?.line1 || prop.name || 'Unnamed Property'}
                                                </div>
                                                <div className="text-[10px] text-slate-500">
                                                    {prop.address?.postcode || 'No postcode'}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="pt-4 border-t border-slate-200 flex gap-3">
                                    <button 
                                        onClick={() => setEditingGroupId(null)}
                                        className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-600 text-sm font-bold hover:bg-white transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        onClick={saveProperties}
                                        disabled={saving}
                                        className="flex-1 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-bold hover:bg-slate-800 disabled:opacity-50 transition-colors shadow-lg"
                                    >
                                        {saving ? 'Saving...' : 'Save Assignments'}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="font-bold text-slate-700">Bulk Management</h3>
                                    <div className="flex gap-2">
                                        <button 
                                            onClick={() => {
                                                if (selectedPropertyIds.size === properties.length) setSelectedPropertyIds(new Set());
                                                else setSelectedPropertyIds(new Set(properties.map(p => p.id)));
                                            }}
                                            className="text-[10px] font-bold text-slate-500 uppercase hover:text-slate-800"
                                        >
                                            {selectedPropertyIds.size === properties.length ? 'Deselect All' : 'Select All'}
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="flex-1 overflow-y-auto space-y-1 mb-4 pr-2">
                                    {properties.map(prop => (
                                        <div 
                                            key={prop.id}
                                            className={`flex items-center gap-3 p-2 rounded-lg transition-colors cursor-pointer ${selectedPropertyIds.has(prop.id) ? 'bg-red-50' : 'hover:bg-slate-200'}`}
                                            onClick={() => toggleProperty(prop.id)}
                                        >
                                            <input 
                                                type="checkbox" 
                                                checked={selectedPropertyIds.has(prop.id)}
                                                onChange={() => {}} 
                                                className="h-4 w-4 rounded text-red-600 focus:ring-red-500"
                                            />
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium text-slate-800 truncate">
                                                    {prop.address?.line1 || prop.name || 'Unnamed Property'}
                                                </div>
                                                <div className="text-[10px] text-slate-500">
                                                    {prop.address?.postcode || 'No postcode'}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {properties.length === 0 && (
                                        <div className="text-center py-12 text-slate-400 italic text-sm">
                                            No properties found in portfolio.
                                        </div>
                                    )}
                                </div>

                                {selectedPropertyIds.size > 0 && (
                                    <div className="p-4 bg-red-50 border border-red-100 rounded-xl mb-4 animate-in fade-in slide-in-from-bottom-2">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="text-sm font-bold text-red-900">{selectedPropertyIds.size} properties selected</div>
                                                <div className="text-[10px] text-red-600 font-medium">This action cannot be undone.</div>
                                            </div>
                                            <button 
                                                onClick={async () => {
                                                    if (!window.confirm(`Are you sure you want to delete ${selectedPropertyIds.size} properties?`)) return;
                                                    setSaving(true);
                                                    try {
                                                        await complianceService.deletePropertiesBulk({ 
                                                            propertyIds: Array.from(selectedPropertyIds) 
                                                        });
                                                        setSelectedPropertyIds(new Set());
                                                        fetchData();
                                                        onRefresh();
                                                    } catch (err) {
                                                        alert('Failed to delete properties');
                                                    } finally {
                                                        setSaving(false);
                                                    }
                                                }}
                                                disabled={saving}
                                                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 disabled:opacity-50 transition-colors shadow-lg"
                                            >
                                                {saving ? 'Deleting...' : 'Delete Selected'}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className="mt-auto pt-4 border-t border-slate-200">
                                    <button 
                                        onClick={async () => {
                                            if (!window.confirm('CRITICAL: Are you sure you want to WIPE your entire portfolio? This will delete all 600+ properties and all associated certificates.')) return;
                                            setSaving(true);
                                            try {
                                                await complianceService.deletePropertiesBulk({ deleteAll: true });
                                                fetchData();
                                                onRefresh();
                                            } catch (err) {
                                                alert('Failed to wipe portfolio');
                                            } finally {
                                                setSaving(false);
                                            }
                                        }}
                                        disabled={saving}
                                        className="w-full py-3 border-2 border-dashed border-red-200 text-red-400 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-red-50 hover:border-red-400 hover:text-red-600 transition-all"
                                    >
                                        <i className="fas fa-exclamation-triangle mr-2"></i>
                                        Wipe Entire Portfolio
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
};

export default PropertyGroupManager;
